/**
 * Shared public-link helpers — the single source of truth for where the
 * marketing site sends visitors to LOG IN or SIGN UP.
 *
 * Why this exists
 * ----------------
 * PCNshark runs on two hosts: the marketing site (`pcnshark.com`) and the
 * product (`app.pcnshark.com`). The marketing-host middleware returns a 404
 * for any path that isn't on its small allowlist, and `/login` + `/signup`
 * live ONLY on the app host. So a relative `<a href="/login">` in the nav,
 * footer, or a pricing card 404s in production. These helpers prefix the
 * app-host origin so every public auth link resolves to the right host.
 *
 * Client-safe by design: reads `NEXT_PUBLIC_APP_URL`, which Next inlines into
 * the browser bundle at build time. Server components may use it too. It
 * mirrors the server-only `getAppHostUrl()` (lib/env/app-url.ts), INCLUDING its
 * hardcoded production fallback, so the links resolve to the app host even when
 * NEXT_PUBLIC_APP_URL wasn't set at build time. Resolution:
 *
 *   1. NEXT_PUBLIC_APP_URL if set (e.g. http://localhost:3000 for local dev, or
 *      a non-standard app host) — overrides everything.
 *   2. Otherwise the canonical production app host, https://app.pcnshark.com.
 *
 * (APP_HOSTS, the server helper's first tier, isn't exposed to the client.)
 *
 * Keep ALL public login/signup hrefs going through these helpers — don't
 * hardcode the app hostname in components.
 */

/** Self-serve tiers a public CTA can preselect. Enterprise books a call. */
export type SignupPlan = "starter" | "team" | "scale";

const SIGNUP_PLANS: readonly SignupPlan[] = ["starter", "team", "scale"];

/**
 * Narrow an untrusted value (URL param, form field, stored metadata) to a
 * known self-serve plan, or null. The single validator shared by the signup
 * page, the signUp action, and /start-trial so they can't drift.
 */
export function asSignupPlan(value: unknown): SignupPlan | null {
  return typeof value === "string" &&
    (SIGNUP_PLANS as readonly string[]).includes(value)
    ? (value as SignupPlan)
    : null;
}

/** Canonical production app host. */
const PROD_APP_HOST_URL = "https://app.pcnshark.com";

/**
 * App-host origin (no trailing slash) for public auth links.
 *
 * Deployed builds (NODE_ENV=production) ALWAYS target the app host —
 * hardcoded, with NO env-var dependency, so nothing in the deploy config can
 * point these links at the marketing host. Only local `next dev` honors
 * NEXT_PUBLIC_APP_URL (else relative paths) so localhost works without an app
 * subdomain. NEXT_PUBLIC_APP_URL is NOT read in production and is unrelated to
 * any Cal.com / booking variable.
 */
function appBase(): string {
  if (process.env.NODE_ENV === "production") return PROD_APP_HOST_URL;
  return process.env.NEXT_PUBLIC_APP_URL?.replace(/\/+$/, "") ?? "";
}

/** Canonical public "Log in" destination (always on the app host). */
export function loginUrl(): string {
  return `${appBase()}/login`;
}

/**
 * Canonical public "Sign up" destination. An optional `plan` is carried as
 * `?plan=` so the funnel can preselect it (and is also persisted to the
 * user's profile at signup, since the URL param does not survive the email-
 * confirmation round-trip — see app/actions/auth.ts:signUp).
 */
export function signupUrl(opts?: { plan?: SignupPlan; email?: string }): string {
  const params = new URLSearchParams();
  if (opts?.plan) params.set("plan", opts.plan);
  if (opts?.email) params.set("email", opts.email);
  const qs = params.toString();
  return `${appBase()}/signup${qs ? `?${qs}` : ""}`;
}
